import React, { useState } from "react";
import {
  CardViewX,
  Typography,
  Tag,
  Avatar,
  Icon,
  IconHighlight,
} from "@ann/web-components";
import { NameInitialsAvatar } from "react-name-initials-avatar";
import {
  getAvatarUrl,
  generateAvatarColor,
} from "../../../../../../../../../../utils/avatarUtils";
import {
  HeaderTitleWrapper,
  DescriptionRow,
  AdminOwnerSection,
  AdminOwnerInfo,
  AvatarWithInitialsWrapper,
  ParentDepartmentBadge,
  LastUpdatedRow,
  Divider,
} from "./SubDepartmentCard.styled";

const formatDate = (dateString) => {
  if (!dateString) return "Unknown";
  try {
    return new Date(dateString).toISOString().split("T")[0];
  } catch {
    return "Unknown";
  }
};

const getValue = (obj, ...keys) => {
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return null;
};

const SubDepartmentCard = ({ subDepartment, onClick }) => {
  const [imageFailed, setImageFailed] = useState(false);

  const name =
    getValue(subDepartment, "departmentName", "sub_department_name", "department_name", "name") ||
    "Unnamed Sub-Department";
  const description = getValue(subDepartment, "description") || "";
  const parentName =
    getValue(
      subDepartment,
      "parent_department_name",
      "parent_department",
      "name"
    ) || "";
  const status =
    getValue(subDepartment, "status", "department_status") || "ACTIVE";
  const lastUpdated = getValue(
    subDepartment,
    "updatedAt",
    "updated_at",
    "last_updated",
    "createdAt",
    "created_at"
  );
  const adminName =
    getValue(
      subDepartment,
      "ownerName",
      "owner_name",
      "admin_owner_name",
      "admin_owner",
      "name"
    ) || "";
  const photoUrl = getValue(
    subDepartment,
    "ownerPhotoUrl",
    "owner_photo_url",
    "photo_url",
    "admin_owner_photo_url",
    "admin_owner"
  );

  const avatarUrl = imageFailed ? null : getAvatarUrl(photoUrl, adminName);
  const avatarColor = generateAvatarColor(adminName);
  const hasAvatar = !imageFailed && avatarUrl;
  const statusColor =
    status === "ACTIVE"
      ? "success"
      : status === "INACTIVE"
      ? "error"
      : "informative";

  const handleClick = () => onClick?.(subDepartment);
  const handleImageError = () => setImageFailed(true);

  return (
    <CardViewX size="medium" onClick={handleClick}>
      <CardViewX.Header
        title={
          <HeaderTitleWrapper>
            <IconHighlight
              bgColor="primary"
              icon="group"
              iconColor="primary"
              size="medium"
            />
            <span>{name}</span>
            <Tag color={statusColor} size="small" text={status} />
          </HeaderTitleWrapper>
        }
      />

      {description && (
        <CardViewX.Description>
          <Typography
            category="body"
            size="small"
            weight="regular"
            color="body"
          >
            {description}
          </Typography>
        </CardViewX.Description>
      )}

      <CardViewX.Meta>
        <DescriptionRow>
          {parentName && (
            <ParentDepartmentBadge>
              <Tag color="informative" size="small" text={parentName} />
            </ParentDepartmentBadge>
          )}
          <LastUpdatedRow>
            <Icon name="lastUpdated" size="small" />
            <Typography
              category="body"
              size="small"
              weight="regular"
              color="body"
            >
              Last updated {formatDate(lastUpdated)}
            </Typography>
          </LastUpdatedRow>
        </DescriptionRow>
      </CardViewX.Meta>

      <Divider />

      <CardViewX.Actions>
        <AdminOwnerSection>
          {hasAvatar ? (
            <Avatar
              alt={adminName}
              size="md"
              src={avatarUrl}
              onError={handleImageError}
            />
          ) : (
            <AvatarWithInitialsWrapper>
              <NameInitialsAvatar
                name={adminName || "Unknown"}
                bgColor={avatarColor}
                textColor="#ffffff"
                size="32px"
                borderRadius="50%"
                textWeight="bold"
                textSize="12px"
                borderWidth="0"
              />
            </AvatarWithInitialsWrapper>
          )}
          <AdminOwnerInfo>
            <Typography category="body" size="small" weight="regular">
              {adminName || "Unknown Admin"}
            </Typography>
            <Typography
              category="body"
              size="small"
              weight="regular"
              color="body"
            >
              Admin
            </Typography>
          </AdminOwnerInfo>
        </AdminOwnerSection>
      </CardViewX.Actions>
    </CardViewX>
  );
};

export default SubDepartmentCard;
